import React from 'react';
import { ResponsiveECharts } from './ResponsiveECharts';
import { ChartCard } from '../ChartCard';
import '../studysyncEChartsTheme'; // side effect: registers the 'studysync' theme before render
import { tokens } from '../tokens';
import { ECHARTS_ANIM, markStagger } from '../motion';
import type { ChartSpec, ChartData } from '../copilotTypes';

type HistogramSpec = Extract<ChartSpec, { chart: 'histogram' }>;
type HistogramData = Extract<ChartData, { chart: 'histogram' }>;

export function buildHistogramOption(spec: HistogramSpec, data: HistogramData) {
  const { values, stats } = data;
  const bins = Math.min(spec.bins, Math.max(5, Math.floor(values.length / 2)));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const width = (max - min) / bins || 1;
  const counts = new Array(bins).fill(0);
  for (const v of values) {
    let i = Math.floor((v - min) / width);
    if (i >= bins) i = bins - 1;
    if (i < 0) i = 0;
    counts[i] += 1;
  }
  const labels = counts.map((_, i) => Math.round(min + (i + 0.5) * width).toLocaleString());
  const binOf = (val: number) => Math.min(bins - 1, Math.max(0, Math.floor((val - min) / width)));

  // Each requested marker (mean / median) is drawn as a dashed vertical line with a label that sits
  // ABOVE the plot area, never over the bars or the x-axis. To guarantee the labels never collide
  // with EACH OTHER when their bins are close, we stack them on two distinct rows (one per marker)
  // and align them to opposite sides — so even adjacent-bin markers stay legible. `grid.top` reserves
  // the vertical room for both rows above the plot.
  const requested = (['mean', 'median'] as const).filter((m) => spec.markers.includes(m));
  // Each marker gets a DISTINCT color + line style so Mean and Median are tellable apart at a glance
  // — no hover needed. Labels stack on separate rows above the plot and inherit their line's color.
  const ROW_HEIGHT = 18;
  const MARK_STYLE = {
    mean: { color: tokens.brand.cardinal, type: 'dashed' as const },
    median: { color: tokens.text.muted, type: 'dotted' as const },
  };
  const markData: any[] = requested.map((m, idx) => {
    const value = m === 'mean' ? stats.mean : stats.median;
    const style = MARK_STYLE[m];
    return {
      xAxis: binOf(value),
      lineStyle: { color: style.color, type: style.type, width: 1.5 },
      label: {
        position: 'end',
        distance: 6 + idx * ROW_HEIGHT,
        // Centered over each line (they're on separate rows, so they never collide) — keeps each
        // label's color sitting directly over its own line instead of crossing toward the other.
        align: 'center',
        color: style.color,
        formatter: `${m === 'mean' ? 'Mean' : 'Median'} ${Math.round(value).toLocaleString()}`,
      },
    };
  });

  // Reserve just enough headroom above the plot for the stacked marker label rows so they never
  // clip, without leaving an oversized empty band: one tight row of clearance per marker.
  const gridTop = 12 + requested.length * ROW_HEIGHT;

  return {
    ...ECHARTS_ANIM,
    grid: { left: 4, right: 12, top: gridTop, bottom: 24, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      // Two-line tooltip mirrors the calendar's: a header line that names the bucket (the bin center
      // + metric unit), then a bold measured value (the number of days that fall in this bin).
      formatter: (params: any) => {
        const p = Array.isArray(params) ? params[0] : params;
        const n = Number(p.value) || 0;
        return `~${p.axisValue} ${data.unit}<br/><strong>${n.toLocaleString()}</strong> day${n === 1 ? '' : 's'}`;
      },
    },
    xAxis: {
      type: 'category',
      data: labels,
      name: data.unit,
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { color: tokens.text.secondary, fontFamily: tokens.fontFamily, fontSize: 12 },
      axisLabel: { interval: Math.ceil(bins / 8), hideOverlap: true },
    },
    yAxis: { type: 'value', name: 'days' },
    series: [
      {
        type: 'bar',
        data: counts,
        barWidth: '94%',
        // Bars sweep in left→right; for a sparse histogram each bin is offset slightly, dense ones fade
        // together. `n` is the bin count captured here, not read from the ECharts callback.
        animationDelay: markStagger(counts.length),
        markLine: markData.length
          ? {
              symbol: 'none',
              label: { fontFamily: tokens.fontFamily },
              data: markData,
            }
          : undefined,
      },
    ],
  };
}

// React.memo + a memoized option: the transcript re-renders on every streamed token, and an inline
// buildHistogramOption() each render makes a fresh option object that defeats ResponsiveECharts' useMemo,
// forcing a setOption(notMerge) reflow. Stable spec/data -> stable option -> no reflow during streaming.
export const Histogram = React.memo(function Histogram({ title, spec, data }: { title: string; spec: HistogramSpec; data: HistogramData }) {
  const option = React.useMemo(() => buildHistogramOption(spec, data), [spec, data]);
  return (
    <ChartCard title={title} downloadable>
      <ResponsiveECharts theme="studysync" option={option} style={{ height: 280 }} notMerge />
    </ChartCard>
  );
});
